import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, avatarUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Card, Label, Input, Textarea } from "@/components/shared";
import { UserCircle, Upload, Globe, Save, PlayCircle, FileSignature } from "lucide-react";
import { TutorialModal } from "@/components/TutorialModal";
import { DocumentManager } from "@/components/DocumentManager";
import { ChangePasswordCard } from "@/components/ChangePasswordCard";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [company, setCompany] = useState(user?.company || "");
  const [website, setWebsite] = useState(user?.website || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [address, setAddress] = useState(user?.address || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [picFileId, setPicFileId] = useState(user?.picture_file_id || null);
  const [saving, setSaving] = useState(false);
  const [uploadingPic, setUploadingPic] = useState(false);
  const [showTut, setShowTut] = useState(false);
  const [signing, setSigning] = useState(false);
  const [agreeChecked, setAgreeChecked] = useState(false);
  const isCrew = user?.role === "crew";

  const acceptAgreement = async () => {
    setSigning(true);
    try { await api.post("/me/accept-agreement"); toast.success("Contractor agreement accepted — thank you"); refresh?.(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSigning(false); }
  };

  const picUrl = avatarUrl({ picture_file_id: picFileId, picture: user?.picture });

  const uploadPic = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingPic(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload", fd);
      setPicFileId(up.data.id);
      await api.put("/profile", { picture_file_id: up.data.id });
      toast.success("Photo updated");
      refresh?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploadingPic(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", { name, company, website, phone, address, bio, picture_file_id: picFileId });
      toast.success("Profile saved");
      refresh?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="client-profile-page">
      <div className="animate-rise">
        <div className="font-mono text-xs uppercase tracking-widest text-[#FF5722] mb-1">Account</div>
        <h1 className="font-head font-black uppercase text-3xl sm:text-4xl text-white tracking-tight">My Profile</h1>
        <p className="text-zinc-500 text-sm mt-1">Keep your details up to date — this is what our dispatch team sees for your account.</p>
        <Btn variant="secondary" data-testid="open-tutorial-btn" onClick={() => setShowTut(true)} className="mt-3"><PlayCircle size={16} /> Watch Tutorial & Download Guide</Btn>
      </div>

      <TutorialModal open={showTut} onClose={() => setShowTut(false)} role={user?.role === "crew" ? "crew" : "client"} />

      {isCrew && !user?.agreement_signed && (
        <Card className="p-5 border-2 border-[#FF5722]" data-testid="agreement-reminder">
          <div className="flex items-start gap-3">
            <FileSignature className="text-[#FF5722] shrink-0 mt-0.5" size={22} />
            <div className="flex-1">
              <div className="font-head font-bold uppercase text-white tracking-wide">Contractor agreement pending</div>
              <p className="text-zinc-400 text-sm mt-1">Review and accept the Swift Signings &amp; Services subcontractor agreement to stay eligible to receive and bid on work. By accepting you confirm you will perform work per the agreed scope, pricing and 45-day payment terms, carry your own insurance, and act as an independent contractor.</p>
              <label className="flex items-start gap-2 text-sm text-zinc-300 mt-3 cursor-pointer">
                <input type="checkbox" data-testid="agreement-checkbox" checked={agreeChecked} onChange={(e) => setAgreeChecked(e.target.checked)} className="w-4 h-4 accent-[#FF5722] mt-0.5" />
                <span>I have read and agree to the subcontractor agreement.</span>
              </label>
              <Btn data-testid="accept-agreement-btn" onClick={acceptAgreement} disabled={signing || !agreeChecked} className="mt-3"><FileSignature size={15} /> {signing ? "Saving…" : "Accept Agreement"}</Btn>
            </div>
          </div>
        </Card>
      )}
      {isCrew && user?.agreement_signed && (
        <Card className="p-4" data-testid="agreement-signed">
          <div className="flex items-center gap-2 text-emerald-400 font-mono text-sm"><FileSignature size={16} /> Contractor agreement accepted ✓</div>
        </Card>
      )}

      <Card className="p-5 space-y-5" data-testid="profile-card">
        <div className="flex items-center gap-4">
          {picUrl ? (
            <img src={picUrl} alt="" className="w-20 h-20 object-cover border-2 border-black shadow-[3px_3px_0px_#000]" data-testid="profile-avatar" />
          ) : (
            <div className="w-20 h-20 bg-zinc-800 border-2 border-black shadow-[3px_3px_0px_#000] flex items-center justify-center font-head font-black text-[#FF5722] text-3xl">
              {name?.[0]?.toUpperCase() || <UserCircle size={28} />}
            </div>
          )}
          <label className="inline-flex">
            <input type="file" accept="image/*" className="hidden" data-testid="profile-pic-input" onChange={uploadPic} disabled={uploadingPic} />
            <span className={`inline-flex items-center gap-2 font-head font-bold uppercase tracking-wider text-sm px-4 py-2 border-2 border-zinc-700 bg-zinc-900 text-white cursor-pointer hover:border-[#FF5722] shadow-[3px_3px_0px_#000] ${uploadingPic ? "opacity-50 pointer-events-none" : ""}`}>
              <Upload size={14} /> {uploadingPic ? "Uploading…" : "Upload Photo"}
            </span>
          </label>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Name</Label><Input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div><Label>Company</Label><Input data-testid="profile-company" value={company} onChange={(e) => setCompany(e.target.value)} /></div>
        </div>
        <div>
          <Label>Website</Label>
          <div className="relative">
            <Globe size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
            <Input data-testid="profile-website" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://yourcompany.com" className="pl-9" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Phone</Label><Input data-testid="profile-phone" value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
          <div><Label>Address</Label><Input data-testid="profile-address" value={address} onChange={(e) => setAddress(e.target.value)} /></div>
        </div>
        <div><Label>About you / your company (bio)</Label><Textarea data-testid="profile-bio" value={bio} onChange={(e) => setBio(e.target.value)} placeholder="A short description of who you are and the properties you manage…" /></div>

        <Btn data-testid="save-profile-btn" onClick={save} disabled={saving}><Save size={16} /> {saving ? "Saving…" : "Save Profile"}</Btn>
      </Card>

      <Card className="p-5" data-testid="client-documents-card">
        <DocumentManager basePath="/profile/documents" title="My Documents & Field Specs" hint="Upload field documents for your jobs — winterization labels, checklists & requirements, door postings, photo requirements, price sheets, pay schedules and permit requirements. Dispatch can see and add documents here too." testid="client-doc-manager" />
      </Card>

      <ChangePasswordCard />
    </div>
  );
}
